/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import RightColumns from '../../components/Columns/RightColumns';
import { FlexBox, FlexBoxBetween } from '../../components/tableItems';
import { MainButton } from '../../components/buttons';
import WeeklyLogToolTip from './Components/WeeklyLogToolTip';
import WeeklyLogTable from './Table/WeeklyLogTable';
import DrawerLinks from '../DefaultView/DrawerLinks';
import useActiveUsers from '../../customHooks/useActiveUsers';
import WeeklyLogFilters from './Filters/WeeklyLogFilters';
import useWeeklyLogStore from '../../stores/weeklyLog/WeeklyLogStore';
import {
  exportWeeklyLogs,
  fetchWeeklyLogs,
  postWeeklyFormLog,
  weekCompletion,
} from '../../helpers/weeklyLog';
import FeedbackMessage from '../../components/FeedbackMessage';
import { SeverityType } from '../../models/General';
import { defaultWeeklyParams } from './WeeklyLogModel';
import { UserContextType } from '../../models/User';
import Context from '../../context/UserContext';
import {
  IS_USER_ADMIN,
  IS_USER_ADMIN_MANAGER_STRAT,
} from '../../constants/user';
import WarningWeeklyLogModal from './Components/WarningWeeklyLogModal';

const WeeklyLog = () => {
  const { UserContext, setUserContext } = useContext(
    Context,
  ) as UserContextType;
  const {
    updateLoading,
    updateWeeklyLogFetchResponse,
    updateLoadingSave,
    formWeeklyLog,
    updateWeeklyParams,
    weeklyReqParams,
    updateWeeklyLogAvgTotalResponse,
    loading,
    loadingSave,
  } = useWeeklyLogStore();
  const isFirstRender = useRef(true);

  const [warningModal, setWarningModal] = useState(false);
  const openWarningModal = () => setWarningModal(true);
  const closeWarningModal = () => setWarningModal(false);

  const { owners: users } = useActiveUsers();
  const [filterDrawer, setFilterDrawer] = useState(false);
  const openFilterDrawer = () => setFilterDrawer(true);
  const closeFilterDrawer = () => setFilterDrawer(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<{
    message: string;
    severity: SeverityType;
  }>({ message: '', severity: 'success' });

  const getWeeklyLogResponse = async (
    start_date?: string,
    end_date?: string,
  ) => {
    updateLoading(true);
    try {
      const resp = await fetchWeeklyLogs(
        start_date ?? weeklyReqParams.start_date,
        end_date ?? weeklyReqParams.end_date,
        weeklyReqParams.owner,
        weeklyReqParams.prev_week === 0 ? undefined : weeklyReqParams.prev_week,
      );
      updateWeeklyLogAvgTotalResponse(resp.totals, resp.averages);
      updateWeeklyLogFetchResponse(resp.weekly_log_records);
    } catch (err) {
      updateWeeklyLogFetchResponse([]);
    }
  };

  const exportWeek = async (start_date?: string, end_date?: string) => {
    setLoadingExport(true);
    try {
      await exportWeeklyLogs(
        start_date ?? weeklyReqParams.start_date,
        end_date ?? weeklyReqParams.end_date,
        weeklyReqParams.owner,
        weeklyReqParams.prev_week === 0 ? undefined : weeklyReqParams.prev_week,
      );
      setFeedbackMessage({
        message: 'Export file will be sent to your email.',
        severity: 'success',
      });
      setFeedbackOpen(true);
    } catch (err) {
      setFeedbackMessage({
        message: 'There was an error exporting your file',
        severity: 'error',
      });
      setFeedbackOpen(true);
    } finally {
      setLoadingExport(false);
    }
  };

  const saveForm = async () => {
    updateLoadingSave(true);
    try {
      const filterValues = formWeeklyLog.filter(
        item => item.follow_ups !== null || item.responses_sent !== null,
      );
      await postWeeklyFormLog(filterValues);
      setFeedbackMessage({
        message: 'Week was updated sucessfully',
        severity: 'success',
      });
      setFeedbackOpen(true);

      if (!UserContext.logs_completion.overall_completion) {
        const completed = await weekCompletion(
          weeklyReqParams.start_date,
          weeklyReqParams.end_date,
        );
        if (completed.overall_completion) {
          setUserContext({
            ...UserContext,
            logs_completion: {
              ...UserContext.logs_completion,
              overall_completion: true,
            },
          });
          updateWeeklyParams({ start_date: undefined, end_date: undefined });
        }
      } else {
        getWeeklyLogResponse();
      }
    } catch (err) {
      setFeedbackMessage({
        message: 'There was an error while updating the week.',
        severity: 'error',
      });
      setFeedbackOpen(true);
    } finally {
      updateLoadingSave(false);
    }
  };
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    getWeeklyLogResponse();
  }, [weeklyReqParams]);

  useEffect(() => {
    if (!UserContext.logs_completion.overall_completion) {
      openWarningModal();
      updateWeeklyParams({
        start_date: UserContext.logs_completion.start_date,
        end_date: UserContext.logs_completion.end_date,
      });
    } else getWeeklyLogResponse();

    return () => {
      updateLoading(true);
      updateLoadingSave(false);
      updateWeeklyParams(defaultWeeklyParams);
    };
  }, []);

  return (
    <>
      <DrawerLinks
        openDrawer={filterDrawer}
        openCloseDrawer={closeFilterDrawer}
      >
        <WeeklyLogFilters users={users} closeFilterDrawer={closeFilterDrawer} />
      </DrawerLinks>
      <div className="right_columns">
        <RightColumns pr={0} pl={0}>
          <FlexBoxBetween px={2}>
            <FlexBox>
              <IconButton
                aria-label="filter"
                id="long-button"
                sx={{
                  border: 1,
                  borderRadius: '10%',
                }}
                aria-haspopup="true"
                onClick={openFilterDrawer}
                disabled={
                  !UserContext.logs_completion.overall_completion &&
                  !IS_USER_ADMIN.includes(UserContext.profile.role)
                }
              >
                <FilterListIcon
                  sx={{
                    color: 'black',
                    '&:hover': { color: 'green' },
                  }}
                />
              </IconButton>
              <Typography ml={1} fontSize={17} fontWeight="bold">
                Filters
              </Typography>
            </FlexBox>
            <FlexBox>
              <Typography fontSize={24} fontWeight={900}>
                Weekly Linkbuilding Log
              </Typography>
              <WeeklyLogToolTip />
            </FlexBox>
            {!IS_USER_ADMIN_MANAGER_STRAT &&
              !UserContext.profile.can_export && <div />}
            {(IS_USER_ADMIN_MANAGER_STRAT ||
              UserContext.profile.can_export) && (
              <MainButton
                disabled={loadingExport || loading || loadingSave}
                variant="contained"
                endIcon={<DownloadIcon />}
                type="submit"
                loading={loadingExport}
                sx={{
                  mr: 2,
                  backgroundColor: '#84D0F0',
                  color: 'black',
                  '&:hover': {
                    backgroundColor: '#84D0F0',
                  },
                }}
                onClick={() => exportWeek()}
              >
                Export
              </MainButton>
            )}
          </FlexBoxBetween>
          <Box mt={3}>
            <WeeklyLogTable saveForm={saveForm} />
          </Box>
        </RightColumns>
      </div>
      <FeedbackMessage
        open={feedbackOpen}
        setOpen={setFeedbackOpen}
        vertical="top"
        horizontal="center"
        severity={feedbackMessage.severity}
        message={feedbackMessage.message}
      />
      <WarningWeeklyLogModal
        open={warningModal}
        closeModal={closeWarningModal}
      />
    </>
  );
};

export default WeeklyLog;
